import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PinPreview } from "../components/PinPreview";
import { palettes, typography } from "@shared/templates";
import type { PinInput, TemplateName } from "@shared/templates/types";
import "../landing.css";

// Landing — public "business card" pages for the Pinterest-automation
// concierge service. The root "/" is a compact vertical chooser (hub);
// each vertical gets its own full pitch page built from a LandingProfile:
//   /weddings  — wedding venues & restaurants (the original landing)
//   /interiors — interior designers, home stagers, renovation firms
// Their only job: make a prospect who got the cold email believe this is a
// real service run by a real person, so they reply. No auth, no checkout —
// conversion happens on the call. Sample pins are rendered LIVE by the real
// engine (PinPreview = client-side template render), so the "this is our
// actual output" claim is literally true and there are no images to host.

// ── Edit these as the business evolves ──
const CONTACT_EMAIL = "dmitri@sauntera.com";
const FOUNDER_NAME = "Dmitri";
const PRICE = "$149";
// Paste your Stripe links here once set up in the Stripe dashboard:
//   • STRIPE_CHECKOUT_URL — a Payment Link for the $149/mo subscription.
//   • STRIPE_PORTAL_URL   — the Customer Portal link (manage / cancel).
// Leave empty to hide the buttons (samples-first flow still works).
const STRIPE_CHECKOUT_URL = "https://buy.stripe.com/aFa6oIfBHbrYb32fak5sA01";
const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/eVqdRa9djeEa1ss8LW5sA00";

const MAILTO = `mailto:${CONTACT_EMAIL}?subject=Pinterest%20pilot%20%E2%80%94%20free%20samples&body=Hi%20${FOUNDER_NAME}%2C%20I%27d%20like%20to%20see%20sample%20pins%20for%20my%20business.`;

// ── Vertical profiles ────────────────────────────────────────────────
// Everything that differs between the /weddings and /interiors pages lives
// here; the page skeleton (steps, report, proof, founder, FAQ, pricing,
// form) is shared by VerticalLanding below.

type LeadOption = {
  value: string;              // select value (unique per option)
  label: string;              // human label; also sent as `segment`
  vertical: string;           // backend ClientVertical the option maps to
};

type LandingProfile = {
  path: string;               // route path, also the canonical
  docTitle: string;
  metaDescription: string;
  ldDescription: string;
  h1: string;
  lede: string;
  heroPin: { template: TemplateName; data: PinInput };
  samplesSub: string;
  samplePins: Array<{ template: TemplateName; data: PinInput }>;
  monthMix: Array<{ week: string; title: string; body: string }>;
  leadOptions: LeadOption[];
  namePlaceholder: string;
  report: {
    biz: string;
    summary: string;
    pins: [string, string, string];
  };
  /** Small cross-link to the sibling vertical, shown above the footer. */
  crossLink: { text: string; to: string; cta: string };
};

// Live demo pins — real engine output rendered client-side by PinPreview.
// Images live in dashboard/public/demo (root-relative URLs resolve inside
// the srcDoc iframe against the site origin). Fictional business names so
// there are no rights questions; captions match each photo's content,
// exactly as the real generator would pair them.

const WEDDINGS_PROFILE: LandingProfile = {
  path: "/weddings",
  docTitle: "Sauntera Pins — done-for-you Pinterest for GTA venues",
  metaDescription:
    "Done-for-you Pinterest for Greater Toronto Area wedding venues and restaurants — 30 branded pins made from your photos, posted daily. $149/month, cancel anytime.",
  ldDescription:
    "Done-for-you Pinterest content for Greater Toronto Area wedding and dining venues — branded pins created and posted monthly.",
  h1: "Pinterest on autopilot for wedding venues & restaurants.",
  lede:
    "We generate the pins, you approve them, and we post a fresh one to " +
    "your Pinterest every day. Couples and diners plan on Pinterest — " +
    "consistency is what gets you found there, and we handle all of it.",
  // Hero uses the overlay layout — text floats at the top over a soft scrim
  // so the photo stays the hero, with the script monogram + CTA in the footer.
  heroPin: {
    template: "venueOverlay",
    data: {
      brand: "Maple & Vine Estate",
      eyebrow: "CALEDON WEDDINGS",
      cta: "See the venue",
      imageUrl: "/demo/wedding-ceremony.jpg",
      palette: palettes.softPastel,
      typography: typography.warmModern,
      venue: {
        name: "Say 'I do' at Maple & Vine",
        location: "Caledon",
        tagline: "Where the day comes together",
      },
    },
  },
  samplesSub:
    "Every pin below was generated by our system — branded, on-theme, and " +
    "ready to post. Your pins look like this, with your photos.",
  // Sample row shows FOUR DISTINCT layouts (the hero above is the overlay, so
  // with it the page demos five looks): editorial script, branded feature card,
  // the Barn-vs-Ballroom comparison, and a dining overlay. Variety is the
  // pitch — a client's board rotates through these, not one template stamped
  // 30 times.
  samplePins: [
    {
      template: "venueScript",
      data: {
        brand: "Rosewood Hall",
        cta: "See the venue",
        imageUrl: "/demo/wedding-reception.jpg",
        palette: palettes.softPastel,
        typography: typography.warmModern,
        titleAccent: "Rosewood Hall",
        venue: {
          name: "Weddings at Rosewood Hall",
          location: "Toronto",
          tagline: "The space behind the celebrations",
        },
      },
    },
    {
      template: "venueFeature",
      data: {
        brand: "The Glasshouse",
        eyebrow: "VAUGHAN WEDDINGS",
        cta: "See the venue",
        imageUrl: "/demo/wedding-ceremony-area.jpg",
        palette: palettes.sageCream,
        typography: typography.editorial,
        venue: {
          name: "Weddings at The Glasshouse",
          location: "Vaughan",
          tagline: "Where the day comes together",
        },
      },
    },
    {
      template: "venueCompare",
      data: {
        brand: "Ivy & Oak",
        typography: typography.warmModern,
        palette: palettes.softPastel,
        compareQuestion: "Where will you say 'I do'?",
        compare: {
          imageA: "/stock/ballroom_001.jpg",
          labelA: "Ballroom",
          imageB: "/stock/barn_001.jpg",
          labelB: "Barn",
        },
      },
    },
    {
      template: "venueOverlay",
      data: {
        brand: "Lumière",
        eyebrow: "DATE NIGHT · TORONTO",
        cta: "See the menu",
        imageUrl: "/demo/restaurant.jpg",
        palette: palettes.warmTerracotta,
        typography: typography.warmModern,
        venue: {
          name: "Date night at Lumière",
          location: "Toronto",
          tagline: "Low light, good wine, no rush",
        },
      },
    },
  ],
  // The week-by-week mix a real month runs — mirrors the MONTH_PLAN strip on
  // client samples pages, phrased to work for both wedding & dining.
  monthMix: [
    { week: "Week 1", title: "Your space, styled", body: "Hero pins of your best photos — the shots people save to their planning boards." },
    { week: "Week 2", title: "The details", body: "Close-ups, décor, and plates — the saves that keep your name circulating." },
    { week: "Week 3", title: "Planning content", body: "Checklists, guides, and date-night answers — what people actually search on Pinterest." },
    { week: "Week 4", title: "Compare & decide", body: "Comparison and list pins that turn browsers into visits — every pin links to your site." },
  ],
  leadOptions: [
    { value: "wedding", label: "Wedding venue", vertical: "wedding" },
    { value: "dining", label: "Restaurant / bar", vertical: "dining" },
  ],
  namePlaceholder: "e.g. Maple & Vine Estate",
  report: {
    biz: "Maple & Vine Estate",
    summary:
      "Your Pinterest reached 4,820 impressions this month — up 83% as the " +
      "pins gain traction. 38 people saved a pin to their own boards, and " +
      "27 clicked through to your site. Pinterest compounds, so we'll keep " +
      "a fresh pin going out every day.",
    pins: ["/demo/wedding-ceremony.jpg", "/demo/wedding-reception.jpg", "/demo/wedding-venue.jpg"],
  },
  crossLink: {
    text: "Interior designer, home stager, or renovator?",
    to: "/interiors",
    cta: "See Sauntera Pins for interiors →",
  },
};

const INTERIORS_PROFILE: LandingProfile = {
  path: "/interiors",
  docTitle: "Sauntera Pins — done-for-you Pinterest for interior designers & stagers",
  metaDescription:
    "Done-for-you Pinterest for Greater Toronto Area interior designers, home stagers, and renovation firms — 30 branded pins made from your project photos, posted daily. $149/month, cancel anytime.",
  ldDescription:
    "Done-for-you Pinterest content for Greater Toronto Area interior designers, home stagers, and renovation firms — branded pins created and posted monthly.",
  h1: "Pinterest on autopilot for interior designers & home stagers.",
  lede:
    "Homeowners plan renovations, staging, and room refreshes on Pinterest — " +
    "it's where your next client is already searching. We turn your project " +
    "photos into branded pins and post a fresh one every day, so your work " +
    "gets found.",
  heroPin: {
    template: "venueOverlay",
    data: {
      brand: "Harlowe Interiors",
      eyebrow: "TORONTO INTERIORS",
      cta: "See the project",
      imageUrl: "/demo/interior-hero.jpg",
      palette: palettes.sageCream,
      typography: typography.editorial,
      venue: {
        name: "The King West living room",
        location: "Toronto",
        tagline: "Dark, warm, deliberate",
      },
    },
  },
  samplesSub:
    "Every pin below was generated by our system — branded, on-theme, and " +
    "ready to post. Your pins look like this, with your project photos.",
  // Four distinct layouts, matching the weddings page: editorial script,
  // branded feature card, the Before/After comparison (the interiors-specific
  // beat — transformation pins are the highest-save format in the reno
  // space), and a concept overlay. Photos are licensed stock; the "before"
  // shot was generated to match the "after" room for a credible pair.
  samplePins: [
    {
      template: "venueScript",
      data: {
        brand: "Oak & Ash Design Co.",
        cta: "See the project",
        imageUrl: "/demo/interior-bath-green.jpg",
        palette: palettes.sageCream,
        typography: typography.warmModern,
        titleAccent: "reimagined",
        venue: {
          name: "The ensuite, reimagined",
          location: "Leaside · Toronto",
          tagline: "Full renovation by Oak & Ash",
        },
      },
    },
    {
      // Staging compare — a REAL vacant listing photo and its staged version
      // of the same room (from the curated Stock Photos/015_Interior Design set).
      template: "venueCompare",
      data: {
        brand: "True North Staging",
        typography: typography.warmModern,
        palette: palettes.softPastel,
        compareQuestion: "Which one sells the home?",
        compare: {
          imageA: "/demo/interior-staging-before.jpg",
          labelA: "Unstaged",
          imageB: "/demo/interior-staging-after.jpg",
          labelB: "Staged",
        },
      },
    },
    {
      template: "venueFeature",
      data: {
        brand: "Marlowe & Finch",
        eyebrow: "TORONTO INTERIOR DESIGN",
        cta: "See the project",
        imageUrl: "/demo/interior-bath-dark.jpg",
        palette: palettes.softPastel,
        typography: typography.editorial,
        venue: {
          name: "A bath with personality",
          location: "Toronto",
          tagline: "Drama, used with confidence",
        },
      },
    },
    {
      template: "venueCompare",
      data: {
        brand: "Rowe Renovations",
        typography: typography.warmModern,
        palette: palettes.sageCream,
        compareQuestion: "What twelve weeks can do",
        compare: {
          imageA: "/demo/interior-bath-before.jpg",
          labelA: "Before",
          imageB: "/demo/interior-bath-after.jpg",
          labelB: "After",
        },
      },
    },
  ],
  monthMix: [
    { week: "Week 1", title: "Your projects, styled", body: "Hero pins of your best rooms — the shots homeowners save to their renovation boards." },
    { week: "Week 2", title: "The details", body: "Materials, textures, and vignettes — the close-ups that keep your name circulating." },
    { week: "Week 3", title: "Ideas content", body: "Guides and checklists people actually search — \"small living room ideas\", \"what staging costs\"." },
    { week: "Week 4", title: "Before & after", body: "Transformation pins that turn browsers into inquiries — every pin links to your site." },
  ],
  leadOptions: [
    { value: "designer", label: "Interior designer", vertical: "interiors" },
    { value: "stager", label: "Home stager", vertical: "interiors" },
    { value: "reno", label: "Renovation / design-build", vertical: "interiors" },
  ],
  namePlaceholder: "e.g. Harlowe Interiors",
  report: {
    biz: "Harlowe Interiors",
    summary:
      "Your Pinterest reached 4,820 impressions this month — up 83% as the " +
      "pins gain traction. 38 people saved a pin to their own boards, and " +
      "27 clicked through to your portfolio. Pinterest compounds, so we'll " +
      "keep a fresh pin going out every day.",
    pins: ["/demo/interior-living.jpg", "/demo/interior-kitchen.jpg", "/demo/interior-bedroom.jpg"],
  },
  crossLink: {
    text: "Wedding venue or restaurant?",
    to: "/weddings",
    cta: "See Sauntera Pins for venues →",
  },
};

const STEPS = [
  {
    n: "1",
    title: "Send us your photos",
    body: "A folder of your best photos and your brand colors. Fifteen minutes, once.",
  },
  {
    n: "2",
    title: "Approve the month's pins",
    body: "We generate 30 branded pins from your photos. You approve the batch in about five minutes.",
  },
  {
    n: "3",
    title: "We post & report",
    body: "We set up and brand your boards, schedule a fresh pin a day to your Pinterest, and send a monthly report. You do nothing.",
  },
];

// Host-correct canonical + meta + JSON-LD, set client-side because
// index.html is shared with the weddings host (a static value there would
// mislabel the other domain). Googlebot renders this; non-JS scrapers still
// get the og:* card from index.html.
function usePageMeta(opts: {
  title: string;
  description: string;
  path: string;
  ldDescription: string;
}) {
  useEffect(() => {
    const origin = "https://pins.sauntera.com";
    document.title = opts.title;
    let meta = document.head.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = opts.description;
    let link = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `${origin}${opts.path === "/" ? "/" : opts.path}`;

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-landing", "1");
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "Sauntera Pins",
      url: `${origin}${opts.path === "/" ? "/" : opts.path}`,
      logo: `${origin}/sauntera-pins-black.png`,
      image: `${origin}/og-card.jpg`,
      description: opts.ldDescription,
      areaServed: { "@type": "Place", name: "Greater Toronto Area, Ontario, Canada" },
      email: "dmitri@sauntera.com",
      founder: { "@type": "Person", name: "Dmitri" },
    });
    document.head.appendChild(ld);
    return () => { ld.remove(); };
    // Profile pages mount once per navigation; opts are constant per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.path]);
}

// Lead capture form. POSTs to the public endpoint, which creates a prospect
// Client doc the founder sees in the dashboard. Collects exactly the fields
// needed to generate samples + follow up; everything else is asked in the
// reply. Honeypot ("company") is visually hidden and bot-only.
function LeadForm({ options, namePlaceholder }: { options: LeadOption[]; namePlaceholder: string }) {
  const [state, setState] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [form, setForm] = useState({
    name: "",
    kind: options[0].value,
    email: "",
    city: "",
    website: "",
    company: "", // honeypot
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    // The select's option maps to a backend vertical; the human label rides
    // along as `segment` so "Home stager" vs "Interior designer" isn't lost.
    const opt = options.find((o) => o.value === form.kind) ?? options[0];
    try {
      const res = await fetch("/api/public/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          city: form.city,
          website: form.website,
          company: form.company,
          vertical: opt.vertical,
          segment: opt.label,
        }),
      });
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <div className="lp-form-done">
        <div className="lp-form-check">✓</div>
        <h3>Thanks — I'll be in touch within 24 hours</h3>
        <p>
          I'll put together a few sample pins for {form.name || "your business"} and
          email them to you. No commitment.
        </p>
      </div>
    );
  }

  return (
    <form className="lp-form" onSubmit={submit}>
      <div className="lp-form-row">
        <label>
          Business name
          <input value={form.name} onChange={set("name")} required placeholder={namePlaceholder} />
        </label>
        <label>
          You are a…
          <select value={form.kind} onChange={set("kind")}>
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="lp-form-row">
        <label>
          Email
          <input type="email" value={form.email} onChange={set("email")} required placeholder="you@business.com" />
        </label>
        <label>
          City
          <input value={form.city} onChange={set("city")} required placeholder="Toronto" />
        </label>
      </div>
      <label>
        Website <span className="lp-form-opt">(optional — helps us pull photos)</span>
        <input value={form.website} onChange={set("website")} placeholder="https://…" />
      </label>
      {/* Honeypot — hidden from humans, bots fill it. */}
      <input
        className="lp-hp"
        tabIndex={-1}
        autoComplete="off"
        value={form.company}
        onChange={set("company")}
        aria-hidden="true"
      />
      <button type="submit" className="lp-btn lp-btn-lg" disabled={state === "sending"}>
        {state === "sending" ? "Sending…" : "Get my free samples →"}
      </button>
      {state === "error" && (
        <div className="lp-form-err">
          Something went wrong — please email me directly at{" "}
          <a href={MAILTO}>{CONTACT_EMAIL}</a>.
        </div>
      )}
      <div className="lp-cta-fine">
        No commitment. I'll make samples from your photos and email them over.
      </div>
    </form>
  );
}

// Below-the-fold pin renders are deferred until they're near the viewport —
// each PinPreview boots a full iframe document (fonts + images), and the
// cold-email click is usually mobile. The hero pin stays eager (it IS the LCP).
function LazyPin({ template, data }: { template: TemplateName; data: PinInput }) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || show) return;
    if (!("IntersectionObserver" in window)) { setShow(true); return; }
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) { setShow(true); io.disconnect(); } },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [show]);
  return (
    <div ref={ref} className="lp-pin-card">
      {show ? <PinPreview template={template} data={data} /> : <div className="lp-pin-ph" />}
    </div>
  );
}

function Footer() {
  return (
    <footer className="lp-footer">
      <div className="lp-wrap lp-footer-inner">
        <span>Sauntera Pins · Toronto · © 2026 Sauntera Technologies Inc.</span>
        <span className="legal-footer-links">
          <a href="/terms">Terms</a>
          <a href="/privacy">Privacy</a>
          {STRIPE_PORTAL_URL && <a href={STRIPE_PORTAL_URL}>Manage subscription</a>}
          <a href={MAILTO}>Contact</a>
        </span>
      </div>
    </footer>
  );
}

// ── Full vertical pitch page (/weddings, /interiors) ─────────────────
function VerticalLanding({ profile }: { profile: LandingProfile }) {
  usePageMeta({
    title: profile.docTitle,
    description: profile.metaDescription,
    path: profile.path,
    ldDescription: profile.ldDescription,
  });

  return (
    <div className="landing" id="top">
      {/* ── Nav ── */}
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-inner">
          <Link to="/" className="lp-logo" aria-label="Sauntera Pins">
            <img src="/sauntera-pins-rev.png" alt="Sauntera Pins" className="lp-logo-img" />
          </Link>
          <a href="#apply" className="lp-btn lp-btn-sm">Get free samples</a>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-wrap lp-hero-grid">
          <div className="lp-hero-copy">
            <div className="lp-eyebrow">Pinterest, done for you</div>
            <h1>{profile.h1}</h1>
            <p className="lp-lede">{profile.lede}</p>
            <div className="lp-hero-cta">
              <a href="#apply" className="lp-btn">Get 5 free samples →</a>
              <span className="lp-hero-note">
                {PRICE}/mo · month-to-month · cancel anytime
              </span>
            </div>
          </div>
          <div className="lp-hero-pin">
            <div className="lp-pin-card lp-pin-tilt">
              <PinPreview template={profile.heroPin.template} data={profile.heroPin.data} />
            </div>
          </div>
        </div>
      </section>

      {/* ── Sample pins ── */}
      <section className="lp-section">
        <div className="lp-wrap">
          <h2 className="lp-h2">This is what we make</h2>
          <p className="lp-sub">{profile.samplesSub}</p>
          <div className={`lp-pin-row${profile.samplePins.length === 3 ? " lp-pin-row-3" : ""}`}>
            {profile.samplePins.map((p, i) => (
              <LazyPin template={p.template} data={p.data} key={i} />
            ))}
          </div>
          <h3 className="lp-month-title">A month on your board</h3>
          <div className="lp-month">
            {profile.monthMix.map((w) => (
              <div className="lp-month-card" key={w.week}>
                <div className="lp-month-week">{w.week}</div>
                <h3>{w.title}</h3>
                <p>{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-wrap">
          <h2 className="lp-h2">How it works</h2>
          <div className="lp-steps">
            {STEPS.map((s) => (
              <div className="lp-step" key={s.n}>
                <div className="lp-step-n">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sample monthly report ── */}
      <section className="lp-section">
        <div className="lp-wrap">
          <h2 className="lp-h2">See exactly what it's doing</h2>
          <p className="lp-sub">
            Every month you get a simple report — what your pins reached, what's
            growing, and the month's top performers. No dashboards to learn.
          </p>
          <div className="lp-report-tagwrap">
            <span className="lp-report-tag">Sample report</span>
          </div>
          <div className="lp-report">
            <div className="lp-report-head">
              <div>
                <div className="lp-report-biz">{profile.report.biz}</div>
                <div className="lp-report-sub">Pinterest performance · Month 3</div>
              </div>
              <img className="lp-report-logo" src="/sauntera-pins-rev.png" alt="Sauntera Pins" />
            </div>
            <div className="lp-report-metrics">
              {[
                ["Impressions", "4,820", "▲ 83%"],
                ["Saves", "38", "▲ 81%"],
                ["Outbound clicks", "27", "▲ 93%"],
                ["New followers", "19", "▲ 138%"],
              ].map(([label, num, up]) => (
                <div key={label}>
                  <div className="lp-report-mlabel">{label}</div>
                  <div className="lp-report-num">{num}</div>
                  <div className="lp-report-up">{up}</div>
                </div>
              ))}
            </div>
            <p className="lp-report-summary">{profile.report.summary}</p>
            <div className="lp-report-pins">
              {profile.report.pins.map((src) => (
                <img src={src} alt="" key={src} />
              ))}
            </div>
            <div className="lp-report-foot">
              <span>Sauntera Pins · posted for you</span>
              <span>pins.sauntera.com</span>
            </div>
          </div>
          <p className="lp-cta-fine" style={{ textAlign: "center" }}>
            Sample report with illustrative numbers. Pinterest compounds slowly —
            expect impressions in the first weeks and meaningful traffic by month
            2–3. Month-to-month, so you stay only if the numbers earn it.
          </p>
        </div>
      </section>

      {/* ── Proof ── */}
      <section className="lp-section">
        <div className="lp-wrap lp-proof">
          <div className="lp-proof-stats">
            <div className="lp-stat">
              <div className="lp-stat-n">74,000+</div>
              <div className="lp-stat-l">impressions from a 2-week ad test</div>
            </div>
            <div className="lp-stat">
              <div className="lp-stat-n">$0.41</div>
              <div className="lp-stat-l">cost per click — well under benchmark</div>
            </div>
            <div className="lp-stat">
              <div className="lp-stat-n">30</div>
              <div className="lp-stat-l">branded pins a month, hands-off</div>
            </div>
          </div>
          <p className="lp-proof-note">
            Built and run by the team behind{" "}
            <a href="https://sauntera.com" target="_blank" rel="noopener noreferrer">
              Sauntera
            </a>
            , a Toronto date-planning platform that runs its own Pinterest with
            this exact engine.
          </p>
        </div>
      </section>

      {/* ── Founder ── */}
      <section className="lp-section lp-section-alt">
        <div className="lp-wrap lp-founder">
          <img src="/founder.jpg" alt={FOUNDER_NAME} className="lp-avatar" />
          <div>
            <h2 className="lp-h2 lp-h2-left">A real person runs this</h2>
            <p>
              I'm {FOUNDER_NAME}, based in Toronto. I built the automation that
              powers Sauntera's own Pinterest, and I run this service personally —
              you'll always reach me directly, not a ticket queue. I'm taking on a
              small number of founding customers at the {PRICE}/mo rate.
            </p>
            <a href={MAILTO} className="lp-btn lp-btn-outline">Email me directly</a>
          </div>
        </div>
      </section>

      {/* ── FAQ — the four questions every cold visitor actually has. Answers
           mirror the playbook (access model, expectation line, billing,
           ownership) so page and calls never contradict each other. ── */}
      <section className="lp-section">
        <div className="lp-wrap">
          <h2 className="lp-h2">Common questions</h2>
          <div className="lp-faq">
            <details>
              <summary>Do you need my Pinterest password?</summary>
              <p>
                No. You grant partner access from your own Pinterest settings
                (Settings → Partners) — no passwords change hands, and you can
                revoke access anytime.
              </p>
            </details>
            <details>
              <summary>When will I see results?</summary>
              <p>
                Pinterest compounds slowly — pins keep gaining reach for months
                after they're posted. Expect impressions in weeks 1–4 and
                meaningful traffic by month 2–3. That's why it's month-to-month:
                stay only if the numbers earn it.
              </p>
            </details>
            <details>
              <summary>Can I cancel?</summary>
              <p>
                Anytime. Billing is month-to-month through Stripe, and you can
                cancel yourself from the{" "}
                <a href={STRIPE_PORTAL_URL}>customer portal</a> — no phone call,
                no notice period.
              </p>
            </details>
            <details>
              <summary>Who owns the account and the pins?</summary>
              <p>
                You do. It's your Pinterest account, every pin is made from your
                photos, and each one links to your website. If you ever cancel,
                everything we've posted stays yours.
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* ── Pricing + capture form ── */}
      <section className="lp-section lp-cta-section">
        <div className="lp-wrap lp-cta">
          <h2 className="lp-h2">Founding-customer pricing</h2>
          <div className="lp-price">
            <span className="lp-price-n">{PRICE}</span>
            <span className="lp-price-per">/ month</span>
          </div>
          <ul className="lp-price-list">
            <li>30 branded pins generated &amp; posted every month</li>
            <li>Your Pinterest boards set up &amp; branded — a cohesive, managed profile</li>
            <li>You approve each batch — nothing posts without your say-so</li>
            <li>Monthly performance report</li>
            <li>Month-to-month. Cancel anytime.</li>
          </ul>
          {STRIPE_CHECKOUT_URL && (
            <div className="lp-subscribe">
              <a href={STRIPE_CHECKOUT_URL} className="lp-btn lp-btn-lg">
                Subscribe — {PRICE}/mo →
              </a>
              <div className="lp-cta-fine">
                Secure checkout via Stripe · cancel anytime ·{" "}
                <a href="/terms">Terms</a> &amp; <a href="/privacy">Privacy</a>
              </div>
            </div>
          )}
          {/* Scroll target is the FORM, not the section heading, so the CTA
              lands the visitor on the fields, not the price recap above. */}
          <div className="lp-form-wrap" id="apply">
            <h3 className="lp-form-title">See 5 free samples first</h3>
            <LeadForm options={profile.leadOptions} namePlaceholder={profile.namePlaceholder} />
            <div className="lp-form-alt">
              Prefer email? <a href={MAILTO}>Write to {CONTACT_EMAIL}</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Cross-link to the sibling vertical ── */}
      <div className="lp-crosslink">
        {profile.crossLink.text}{" "}
        <Link to={profile.crossLink.to}>{profile.crossLink.cta}</Link>
      </div>

      <Footer />
    </div>
  );
}

export function LandingWeddings() {
  return <VerticalLanding profile={WEDDINGS_PROFILE} />;
}

export function LandingInteriors() {
  return <VerticalLanding profile={INTERIORS_PROFILE} />;
}

// ── Root hub ("/") — compact vertical chooser ────────────────────────
// Cold emails historically linked the root, so it must still read as the
// service's front door: one line of pitch, then straight to the right page.
export function Landing() {
  const nav = useNavigate();
  usePageMeta({
    title: "Sauntera Pins — done-for-you Pinterest for GTA businesses",
    description:
      "Done-for-you Pinterest for Greater Toronto Area businesses — wedding venues, restaurants, interior designers, and home stagers. 30 branded pins from your photos, posted daily. $149/month.",
    path: "/",
    ldDescription:
      "Done-for-you Pinterest content for Greater Toronto Area businesses — wedding venues, restaurants, interior designers, and home stagers.",
  });

  // Old outreach links pointed at "/#apply" when the full pitch lived on the
  // root — land those visitors on the weddings form instead of a dead anchor.
  useEffect(() => {
    if (window.location.hash === "#apply") nav("/weddings#apply", { replace: true });
  }, [nav]);

  return (
    <div className="landing" id="top">
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-inner">
          <Link to="/" className="lp-logo" aria-label="Sauntera Pins">
            <img src="/sauntera-pins-rev.png" alt="Sauntera Pins" className="lp-logo-img" />
          </Link>
          <a href={MAILTO} className="lp-btn lp-btn-sm">Email {FOUNDER_NAME}</a>
        </div>
      </header>

      <section className="lp-hero lp-hub-hero">
        <div className="lp-wrap" style={{ textAlign: "center" }}>
          <div className="lp-eyebrow">Pinterest, done for you</div>
          <h1>Done-for-you Pinterest for businesses people discover visually.</h1>
          <p className="lp-lede lp-hub-lede">
            We turn your photos into branded pins and post a fresh one to your
            Pinterest every day — {PRICE}/mo, month-to-month. Pick your industry:
          </p>
          <div className="lp-hub-cards">
            <Link to="/weddings" className="lp-hub-card">
              <img src="/demo/wedding-ceremony.jpg" alt="Wedding venue" className="lp-hub-img" loading="lazy" />
              <div className="lp-hub-body">
                <h2>Wedding venues &amp; restaurants</h2>
                <p>Couples and diners plan on Pinterest. Put your space in front of them, every day.</p>
                <span className="lp-hub-go">Pinterest for venues →</span>
              </div>
            </Link>
            <Link to="/interiors" className="lp-hub-card">
              <img src="/demo/interior-living.jpg" alt="Interior design" className="lp-hub-img" loading="lazy" />
              <div className="lp-hub-body">
                <h2>Interior designers, stagers &amp; renovators</h2>
                <p>Homeowners plan every renovation on Pinterest. Get your portfolio into their boards.</p>
                <span className="lp-hub-go">Pinterest for interiors →</span>
              </div>
            </Link>
          </div>
          <p className="lp-cta-fine lp-hub-fine">
            Run personally by {FOUNDER_NAME} in Toronto — built on the engine
            behind{" "}
            <a href="https://sauntera.com" target="_blank" rel="noopener noreferrer">Sauntera</a>.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
